/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { ICON_CHEVRON, Symbol, SymbolDirection } from '@doveit/lego';
import React, {
  FunctionComponent, useContext, useMemo, ReactNode,
} from 'react';
import HAccordionContext from '../../HAccordionContext';
import * as styles from '../../HAccordion.style';

export interface HAccordionBlockProps {
  title: string,
  position?: number,
  backgroundColor?: string,
  backgroundImage?: string,
  children: ReactNode,
}

/** Indicates the number of times the selected block is wider than any block not currently open */
export const OPEN_BLOCK_GROWTH_PROPORTION = 3;

const HAccordionBlock: FunctionComponent<HAccordionBlockProps> = ({
  title,
  position,
  backgroundColor,
  backgroundImage,
  children,
}) => {
  const {
    showOnMouseLeave,
    total,
    openBlockPosition: openedPosition,
    blockHeight,
    updateOpenBlockPosition,
    isMobile,
  } = useContext(HAccordionContext);

  const hasReverseOverlapping = useMemo(
    () => openedPosition && position < openedPosition,
    [openedPosition, position],
  );

  const isOpen = useMemo(
    () => openedPosition === position,
    [openedPosition, position],
  );

  const blockGrowthPercentage = useMemo(
    () => {
      /** flex-grow for all blocks (excluded the selected one) when a block is currently open */
      const resizedGrowth = 100 / (total + OPEN_BLOCK_GROWTH_PROPORTION - 1);

      if (isOpen) {
        return OPEN_BLOCK_GROWTH_PROPORTION * resizedGrowth;
      }

      /** flex-grow for a block when no one block is open */
      const basicGrowth = 100 / total;

      return openedPosition
        ? resizedGrowth
        : basicGrowth;
    },
    [isOpen, openedPosition, total],
  );

  const overlappingIndex = useMemo(
    () => {
      if (isMobile) {
        // the first items will get a greater z-index respectively from top to bottom
        return total - position;
      }

      // open block on desktop view
      if (isOpen) {
        // get the max z-index among the existing blocks
        return total;
      }

      return hasReverseOverlapping
        ? position
        : total - position; // the first items will get a greater z-index respectively from left to right
    },
    [hasReverseOverlapping, isMobile, isOpen, position, total],
  );

  return (
    <li
      data-ref="block"
      css={styles.item}
      $isOpen={isOpen}
      $overlappingIndex={overlappingIndex}
      $blockGrowthPercentage={blockGrowthPercentage}
      $openedPosition={openedPosition}
      $backgroundColor={backgroundColor}
      $backgroundImage={backgroundImage}
      $blockHeight={blockHeight}
      onMouseEnter={!isMobile
        ? updateOpenBlockPosition(position)
        : undefined}
      onMouseLeave={!isMobile && !showOnMouseLeave
        ? updateOpenBlockPosition(undefined)
        : undefined}
    >
      <div
        data-ref="block-title"
        onClick={isMobile
          ? updateOpenBlockPosition(position)
          : undefined}
      >
        {title}
        {isMobile && (
          <Symbol
            path={ICON_CHEVRON}
            direction={SymbolDirection.BOTTOM}
            size={30}
          />
        )}
      </div>
      <div>
        {children}
      </div>
    </li>
  );
};

export default HAccordionBlock;
